import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { CAMPO_BASE } from "./ui";
import Carregando from "./Carregando";
import type { NivelDeAcesso } from "../lib/admin";
import { slugDeTitulo } from "../lib/slug";
import {
  criarGrupo,
  definirConcessao,
  definirExcecao,
  definirMaster,
  editarGrupo,
  excluirGrupo,
  lerMatriz,
  mudarMembro,
  type AreaDoPainel,
  type GrupoDePermissao,
  type Matriz,
  type MudancaDePermissao,
  type RespostaDePermissao,
} from "../lib/permissoes";

/**
 * Admin › Permissões — quem vê e quem edita cada aba do painel.
 *
 * Desde a 000297 é a ÚNICA porta de acesso (pedido de Victor em 25/09): os
 * papéis Admin e Suporte morreram, o admin sobreviveu só como MASTER, e todo o
 * resto é grupo. Cada grupo tem nome, pessoas e um nível por aba (ver ou
 * editar), e a exceção por pessoa continua para quem foge da regra do grupo.
 *
 * A regra do nível mora num lugar só, `meu_nivel()` no banco; esta tela a
 * APRESENTA e chama as RPCs `perm_admin_*`, que recusam sozinhas quem não é
 * master e exigem motivo em todo ato — o motivo vai para o razão.
 *
 * Editar um grupo junta vários atos (renomear, mudar abas, tirar e pôr gente)
 * sob UM motivo, e a tela os manda um a um. Não há transação entre eles; por
 * isso a resposta é a LISTA do que cada um fez, e não um "salvo" que esconderia
 * o ato recusado no meio.
 */
export default function AdminPermissoes() {
  const [matriz, setMatriz] = useState<Matriz | null>(null);
  const [negado, setNegado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const recarregar = useCallback(async () => {
    try {
      const m = await lerMatriz();
      setNegado(!m);
      setMatriz(m);
      setErro(null);
    } catch (e) {
      setErro(e instanceof Error ? e.message : "não foi possível ler as permissões");
    }
  }, []);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  if (negado) {
    return (
      <Caixa titulo="Só o master muda permissões" dado="negado">
        <p className="text-sm text-cinza">
          O banco respondeu vazio para a sua sessão. Esta tela não decide acesso: ela mostra o que
          `perm_admin_ler()` devolve, e ele só responde a quem é master.
        </p>
      </Caixa>
    );
  }
  if (erro && !matriz) {
    return (
      <Caixa titulo="As permissões não vieram" dado="erro">
        <p className="text-sm text-cinza">{erro}</p>
      </Caixa>
    );
  }
  if (!matriz) return <Carregando />;

  return <Conteudo matriz={matriz} recarregar={recarregar} />;
}

const NIVEL_TEXTO: Record<NivelDeAcesso, string> = { nenhum: "Nenhum", ver: "Ver", editar: "Editar" };

/** O resultado de um conjunto de atos, na ordem em que foram mandados. */
type ItemDoResultado = { oQue: string; r: RespostaDePermissao };

function Conteudo({ matriz, recarregar }: { matriz: Matriz; recarregar: () => Promise<void> }) {
  const [editando, setEditando] = useState<string | null>(null);
  const [resultados, setResultados] = useState<Record<string, ItemDoResultado[]>>({});
  const rotuloDa = useCallback(
    (id: string) => matriz.areas.find((a) => a.id === id)?.rotulo ?? id,
    [matriz.areas],
  );

  const guardarResultado = (slug: string, itens: ItemDoResultado[]) =>
    setResultados((atual) => ({ ...atual, [slug]: itens }));

  return (
    <div className="flex flex-col gap-5" data-permissoes>
      <Caixa titulo="Como o acesso é decidido">
        <ol className="list-decimal space-y-1 pl-5 text-sm text-cinza">
          <li>
            O <strong className="text-tinta">master</strong> edita tudo, sempre — é quem administra esta
            tela, e nenhuma regra o tranca para fora.
          </li>
          <li>
            A <strong className="text-tinta">exceção pessoal</strong> vence o grupo — para mais ou para
            menos.
          </li>
          <li>Sem exceção, vale o maior nível entre os grupos da pessoa.</li>
          <li>Fora disso, a pessoa não vê a aba. Quem não vê aba nenhuma não entra no painel.</li>
        </ol>
      </Caixa>

      <Caixa titulo="Grupos" dado="grupos">
        <p className="mb-4 text-sm text-cinza">
          Cada grupo dá às pessoas dele um nível por aba: <strong className="text-tinta">ver</strong> abre
          a aba sem deixar mudar nada, <strong className="text-tinta">editar</strong> deixa mudar. Mudança
          aqui vale na próxima tela que a pessoa abrir.
        </p>
        <div className="flex flex-col gap-4">
          {matriz.grupos.map((g) =>
            editando === g.slug ? (
              <EdicaoDoGrupo
                key={g.slug}
                grupo={g}
                areas={matriz.areas}
                aoTerminar={async (itens) => {
                  setEditando(null);
                  if (itens) guardarResultado(g.slug, itens);
                  await recarregar();
                }}
              />
            ) : (
              <CartaoDoGrupo
                key={g.slug}
                grupo={g}
                areas={matriz.areas}
                resultado={resultados[g.slug]}
                aoEditar={() => {
                  guardarResultado(g.slug, []);
                  setEditando(g.slug);
                }}
              />
            ),
          )}
        </div>
        <NovoGrupo
          existentes={matriz.grupos.map((g) => g.slug)}
          aoCriar={async (slug) => {
            await recarregar();
            setEditando(slug);
          }}
        />
      </Caixa>

      <Masters matriz={matriz} aoMudar={recarregar} />

      <Caixa titulo="Exceções por pessoa" dado="excecoes">
        <p className="mb-3 text-sm text-cinza">
          Para quem foge da regra do grupo: mais do que o grupo dá (cobrir férias) ou menos (só
          consultar este mês). A exceção vence o grupo naquela aba.
        </p>
        {matriz.excecoes.length === 0 ? (
          <p className="text-sm text-cinza">Nenhuma — todo mundo vale pelo grupo.</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {matriz.excecoes.map((e) => (
              <li key={`${e.email}/${e.area}`}>
                <span className="text-tinta">{e.nome}</span>{" "}
                <span className="text-cinza">({e.email})</span> — {NIVEL_TEXTO[e.nivel]} em{" "}
                {rotuloDa(e.area)}
              </li>
            ))}
          </ul>
        )}
        <FormExcecao areas={matriz.areas} aoMudar={recarregar} />
      </Caixa>

      <Caixa titulo="Últimas mudanças" dado="razao">
        {matriz.mudancas.length === 0 ? (
          <p className="text-sm text-cinza">Nenhuma ainda.</p>
        ) : (
          <ul className="divide-y divide-borda text-sm">
            {matriz.mudancas.map((m, i) => (
              <li key={i} className="py-2">
                <span className="mr-2 rounded-full bg-chip px-2 py-0.5 text-xs font-semibold text-tinta">
                  {ROTULO_DO_ALVO[m.alvo] ?? m.alvo}
                </span>
                <span className="text-tinta">{m.alvo_ref}</span>{" "}
                <span className="text-cinza">
                  · {new Date(m.em).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })} · {m.por_descricao}
                </span>
                <span className="block text-cinza">{m.motivo}</span>
              </li>
            ))}
          </ul>
        )}
      </Caixa>
    </div>
  );
}

const ROTULO_DO_ALVO: Record<MudancaDePermissao["alvo"], string> = {
  concessao: "aba do grupo",
  membro: "pessoa no grupo",
  excecao: "exceção",
  grupo: "grupo",
  master: "master",
};

function Caixa({ titulo, dado, children }: { titulo: string; dado?: string; children: ReactNode }) {
  return (
    <section className="surface rounded-cf-lg p-5 sm:p-6" data-caixa={dado} aria-label={titulo}>
      <h2 className="mb-3 font-display text-lg font-bold text-titulo">{titulo}</h2>
      {children}
    </section>
  );
}

/** As abas que o grupo TEM, na ordem do menu. Ausência é "nenhum", e não se lista. */
function abasDoGrupo(g: GrupoDePermissao, areas: AreaDoPainel[]) {
  return areas
    .filter((a) => (g.concessoes[a.id] ?? "nenhum") !== "nenhum")
    .map((a) => ({ area: a, nivel: g.concessoes[a.id] }));
}

function Pessoas({ grupo }: { grupo: GrupoDePermissao }) {
  if (grupo.membros.length === 0) return <p className="text-sm text-cinza">Ninguém ainda.</p>;
  return (
    <ul className="space-y-1 text-sm">
      {grupo.membros.map((m) => (
        <li key={m.email} className="flex flex-wrap items-center gap-2">
          <span className="text-tinta">{m.nome}</span>
          <span className="text-cinza">{m.email}</span>
          {!m.tem_login && (
            <span className="rounded-full bg-chip px-2 py-0.5 text-xs text-cinza">ainda não entrou no hub</span>
          )}
        </li>
      ))}
    </ul>
  );
}

function CartaoDoGrupo({ grupo, areas, resultado, aoEditar }: {
  grupo: GrupoDePermissao; areas: AreaDoPainel[]; resultado?: ItemDoResultado[]; aoEditar: () => void;
}) {
  const abas = abasDoGrupo(grupo, areas);
  return (
    <article className="rounded-xl border border-borda p-4" data-grupo={grupo.slug}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-base font-bold text-titulo">{grupo.nome}</h3>
          {grupo.descricao && <p className="text-sm text-cinza">{grupo.descricao}</p>}
        </div>
        <button type="button" onClick={aoEditar} className="btn-contorno min-h-10 px-4 text-sm"
          aria-label={`Editar o grupo ${grupo.nome}`}>
          Editar
        </button>
      </div>

      <p className="mt-3 text-xs font-bold tracking-wide text-cinza uppercase">Abas</p>
      {abas.length === 0 ? (
        <p className="mt-1 text-sm text-cinza">Nenhuma — quem está aqui não ganha nada ainda.</p>
      ) : (
        <ul className="mt-1 flex flex-wrap gap-2" aria-label={`Abas de ${grupo.nome}`}>
          {abas.map(({ area, nivel }) => (
            <li key={area.id} data-aba={area.id} data-nivel={nivel}
              className="rounded-full bg-chip px-3 py-1 text-sm text-tinta">
              {area.rotulo} · <strong>{NIVEL_TEXTO[nivel]}</strong>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-3 text-xs font-bold tracking-wide text-cinza uppercase">
        Pessoas ({grupo.membros.length})
      </p>
      <div className="mt-1"><Pessoas grupo={grupo} /></div>

      {resultado && resultado.length > 0 && <ListaDeResultados itens={resultado} />}
    </article>
  );
}

function ListaDeResultados({ itens }: { itens: ItemDoResultado[] }) {
  const recusas = itens.filter((i) => !i.r.ok).length;
  return (
    <div role="status" className="mt-3 rounded-cf border border-borda p-3 text-sm" data-resultado={recusas ? "com-recusa" : "ok"}>
      <p className="font-semibold text-tinta">
        {recusas === 0
          ? `${itens.length} ${itens.length === 1 ? "mudança feita" : "mudanças feitas"}.`
          : `${itens.length - recusas} de ${itens.length} feitas — ${recusas} ${recusas === 1 ? "recusada" : "recusadas"}:`}
      </p>
      <ul className="mt-1 space-y-0.5">
        {itens.map((i, n) => (
          <li key={n} className={i.r.ok ? "text-aviso-tinta" : "text-erro-tinta"} data-item={i.r.ok ? "ok" : "recusa"}>
            {i.oQue}: {i.r.detalhe ?? (i.r.ok ? "feito." : "não foi possível.")}
            {i.r.ok && i.r.sem_login && " (ainda não entrou no hub — vale quando entrar)"}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * O grupo em edição. Tudo fica PENDENTE até "Salvar", que manda os atos na
 * ordem — nome, abas, pessoas — sob o mesmo motivo. O resumo embaixo é a lista
 * exata do que vai ser mandado: quem salva sabe o que está mudando.
 */
function EdicaoDoGrupo({ grupo, areas, aoTerminar }: {
  grupo: GrupoDePermissao; areas: AreaDoPainel[]; aoTerminar: (itens: ItemDoResultado[] | null) => Promise<void>;
}) {
  const [nome, setNome] = useState(grupo.nome);
  const [descricao, setDescricao] = useState(grupo.descricao ?? "");
  const [niveis, setNiveis] = useState<Record<string, NivelDeAcesso>>(() =>
    Object.fromEntries(areas.map((a) => [a.id, grupo.concessoes[a.id] ?? "nenhum"])));
  const [saem, setSaem] = useState<string[]>([]);
  const [entram, setEntram] = useState<string[]>([]);
  const [emailNovo, setEmailNovo] = useState("");
  const [motivo, setMotivo] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);

  const nomeMudou = nome.trim() !== grupo.nome;
  const descricaoMudou = descricao.trim() !== (grupo.descricao ?? "");
  const abasMudadas = areas.filter((a) => niveis[a.id] !== (grupo.concessoes[a.id] ?? "nenhum"));

  const mudancas = useMemo(() => {
    const lista: string[] = [];
    if (nomeMudou) lista.push(`nome: ${grupo.nome} → ${nome.trim()}`);
    if (descricaoMudou) lista.push("descrição nova");
    for (const a of abasMudadas) {
      lista.push(`${a.rotulo}: ${NIVEL_TEXTO[grupo.concessoes[a.id] ?? "nenhum"]} → ${NIVEL_TEXTO[niveis[a.id]]}`);
    }
    for (const e of entram) lista.push(`entra: ${e}`);
    for (const e of saem) lista.push(`sai: ${e}`);
    return lista;
  }, [nomeMudou, descricaoMudou, abasMudadas, entram, saem, grupo, nome, niveis]);

  const pode = mudancas.length > 0 && nome.trim().length > 0 && motivo.trim().length > 0 && !salvando;

  const acrescentar = () => {
    const e = emailNovo.trim().toLowerCase();
    if (!e || entram.includes(e)) return;
    if (grupo.membros.some((m) => m.email.toLowerCase() === e)) {
      setSaem((s) => s.filter((x) => x !== e));
    } else {
      setEntram((l) => [...l, e]);
    }
    setEmailNovo("");
  };

  const salvar = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!pode) return;
    setSalvando(true);
    const m = motivo.trim();
    const itens: ItemDoResultado[] = [];
    if (nomeMudou || descricaoMudou) {
      itens.push({ oQue: "nome e descrição", r: await editarGrupo(grupo.slug, nome.trim(), descricao.trim(), m) });
    }
    for (const a of abasMudadas) {
      itens.push({ oQue: a.rotulo, r: await definirConcessao(grupo.slug, a.id, niveis[a.id], m) });
    }
    for (const e of entram) itens.push({ oQue: `entra ${e}`, r: await mudarMembro(grupo.slug, e, true, m) });
    for (const e of saem) itens.push({ oQue: `sai ${e}`, r: await mudarMembro(grupo.slug, e, false, m) });
    setSalvando(false);
    await aoTerminar(itens);
  };

  if (excluindo) {
    return <ExclusaoDoGrupo grupo={grupo} aoCancelar={() => setExcluindo(false)} aoExcluir={aoTerminar} />;
  }

  return (
    <form onSubmit={salvar} className="rounded-xl border-2 border-acento p-4" data-grupo={grupo.slug} data-editando>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm text-cinza">
          Nome do grupo
          <input className={CAMPO_BASE} value={nome} onChange={(e) => setNome(e.target.value)} data-campo="nome" />
        </label>
        <label className="flex flex-col gap-1 text-sm text-cinza">
          Para que serve
          <input className={CAMPO_BASE} value={descricao} onChange={(e) => setDescricao(e.target.value)} data-campo="descricao" />
        </label>
      </div>

      {/* `section role=group` e não `fieldset`: o `legend` flutua sobre a borda
          do card arredondado (a regra do CLAUDE.md, vista na tela em 19/08). */}
      <section className="mt-4" role="group" aria-labelledby={`abas-de-${grupo.slug}`}>
        <h4 id={`abas-de-${grupo.slug}`} className="text-xs font-bold tracking-wide text-cinza uppercase">
          O que o grupo pode em cada aba
        </h4>
        <div className="mt-2 grid gap-2 md:grid-cols-2">
          {areas.map((a) => {
            const mudou = niveis[a.id] !== (grupo.concessoes[a.id] ?? "nenhum");
            return (
              <label key={a.id} data-aba={a.id}
                className={`flex items-center justify-between gap-3 rounded-cf border px-3 py-2 ${mudou ? "border-acento" : "border-borda"}`}>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-tinta">{a.rotulo}</span>
                  {a.descricao && <span className="block text-xs text-cinza">{a.descricao}</span>}
                </span>
                <select
                  className={`${CAMPO_BASE} h-10 shrink-0 px-3 text-sm`}
                  value={niveis[a.id]}
                  aria-label={`Nível de ${grupo.nome} em ${a.rotulo}`}
                  onChange={(e) => setNiveis((n) => ({ ...n, [a.id]: e.target.value as NivelDeAcesso }))}
                >
                  <option value="nenhum">Nenhum</option>
                  <option value="ver">Ver</option>
                  <option value="editar">Editar</option>
                </select>
              </label>
            );
          })}
        </div>
      </section>

      <div className="mt-4">
        <p className="text-xs font-bold tracking-wide text-cinza uppercase">Pessoas</p>
        <ul className="mt-2 space-y-1 text-sm">
          {grupo.membros.map((m) => {
            const sai = saem.includes(m.email);
            return (
              <li key={m.email} className="flex flex-wrap items-center gap-2" data-membro={m.email}>
                <span className={sai ? "text-cinza line-through" : "text-tinta"}>{m.nome}</span>
                <span className={sai ? "text-cinza line-through" : "text-cinza"}>{m.email}</span>
                <button type="button" className="text-sm font-semibold text-link underline-offset-2 hover:underline"
                  onClick={() => setSaem((s) => (sai ? s.filter((x) => x !== m.email) : [...s, m.email]))}>
                  {sai ? "desfazer" : "tirar do grupo"}
                </button>
              </li>
            );
          })}
          {entram.map((e) => (
            <li key={e} className="flex flex-wrap items-center gap-2" data-entra={e}>
              <span className="rounded-full bg-chip px-2 py-0.5 text-xs font-semibold text-tinta">entra</span>
              <span className="text-tinta">{e}</span>
              <button type="button" className="text-sm font-semibold text-link underline-offset-2 hover:underline"
                onClick={() => setEntram((l) => l.filter((x) => x !== e))}>
                desfazer
              </button>
            </li>
          ))}
          {grupo.membros.length === 0 && entram.length === 0 && <li className="text-cinza">Ninguém ainda.</li>}
        </ul>
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <label className="flex min-w-[16rem] flex-1 flex-col gap-1 text-sm text-cinza">
            Pôr alguém (e-mail)
            <input className={CAMPO_BASE} type="email" value={emailNovo} data-campo="email-novo"
              onChange={(e) => setEmailNovo(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); acrescentar(); } }} />
          </label>
          <button type="button" onClick={acrescentar} className="btn-contorno min-h-12 px-4 text-sm"
            disabled={!emailNovo.trim()}>
            Acrescentar
          </button>
        </div>
      </div>

      <label className="mt-4 flex flex-col gap-1 text-sm text-cinza">
        Motivo (obrigatório — vale para todas as mudanças deste grupo)
        <input className={CAMPO_BASE} value={motivo} onChange={(e) => setMotivo(e.target.value)} data-campo="motivo" />
      </label>

      <div className="mt-3 rounded-cf bg-chip p-3 text-sm" data-resumo>
        {mudancas.length === 0 ? (
          <p className="text-cinza">Nada mudou ainda.</p>
        ) : (
          <>
            <p className="font-semibold text-tinta">Vai ser mandado:</p>
            <ul className="mt-1 list-disc pl-5 text-tinta">
              {mudancas.map((m) => <li key={m}>{m}</li>)}
            </ul>
          </>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button type="submit" disabled={!pode} className={pode ? "pill-cta min-h-11 px-5" : "pill-desligada min-h-11 px-5"}>
          {salvando ? "Salvando…" : mudancas.length > 1 ? `Salvar ${mudancas.length} mudanças` : "Salvar"}
        </button>
        <button type="button" onClick={() => void aoTerminar(null)} className="btn-contorno min-h-11 px-4 text-sm" disabled={salvando}>
          Cancelar
        </button>
        <button type="button" onClick={() => setExcluindo(true)} disabled={salvando}
          className="ml-auto text-sm font-semibold text-erro-tinta underline-offset-2 hover:underline">
          Excluir o grupo
        </button>
      </div>
    </form>
  );
}

function ExclusaoDoGrupo({ grupo, aoCancelar, aoExcluir }: {
  grupo: GrupoDePermissao; aoCancelar: () => void; aoExcluir: (itens: ItemDoResultado[] | null) => Promise<void>;
}) {
  const [motivo, setMotivo] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [recusa, setRecusa] = useState<RespostaDePermissao | null>(null);
  const n = grupo.membros.length;

  const excluir = async (ev: FormEvent) => {
    ev.preventDefault();
    setSalvando(true);
    const r = await excluirGrupo(grupo.slug, motivo.trim());
    setSalvando(false);
    if (!r.ok) return setRecusa(r);
    await aoExcluir(null);
  };

  return (
    <form onSubmit={excluir} className="rounded-xl border-2 border-alerta-tinta/50 bg-alerta-fundo p-4" data-grupo={grupo.slug} data-excluindo>
      <p className="font-semibold text-alerta-tinta">Excluir o grupo {grupo.nome}?</p>
      <p className="mt-1 text-sm text-tinta">
        {n === 0
          ? "Ninguém está nele — nenhum acesso muda."
          : `${n} ${n === 1 ? "pessoa deixa" : "pessoas deixam"} de receber o que ele dá, a partir da próxima tela que abrirem.`}{" "}
        O que o grupo dava fica guardado no razão.
      </p>
      <label className="mt-3 flex flex-col gap-1 text-sm text-tinta">
        Motivo (obrigatório)
        <input className={CAMPO_BASE} value={motivo} onChange={(e) => setMotivo(e.target.value)} data-campo="motivo" />
      </label>
      {recusa && <p role="status" className="mt-2 text-sm text-erro-tinta">{recusa.detalhe}</p>}
      <div className="mt-3 flex flex-wrap gap-3">
        <button type="submit" disabled={!motivo.trim() || salvando}
          className={motivo.trim() && !salvando ? "pill-cta min-h-11 px-5" : "pill-desligada min-h-11 px-5"}>
          {salvando ? "Excluindo…" : "Excluir o grupo"}
        </button>
        <button type="button" onClick={aoCancelar} className="btn-contorno min-h-11 px-4 text-sm">Voltar</button>
      </div>
    </form>
  );
}

function NovoGrupo({ existentes, aoCriar }: { existentes: string[]; aoCriar: (slug: string) => Promise<void> }) {
  const [aberto, setAberto] = useState(false);
  const [nome, setNome] = useState("");
  const [descricao, setDescricao] = useState("");
  const [motivo, setMotivo] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [recusa, setRecusa] = useState<RespostaDePermissao | null>(null);
  // O identificador sai do NOME, pela mesma função que dá slug às obrigações do
  // Calendário — a pessoa não precisa saber o que é um slug. Ele é o que o razão
  // guarda, e por isso não muda depois.
  const slug = slugDeTitulo(nome);
  const repetido = existentes.includes(slug);
  const pode = nome.trim().length > 1 && /^[a-z]/.test(slug) && !repetido && motivo.trim().length > 0 && !salvando;

  if (!aberto) {
    return (
      <button type="button" onClick={() => setAberto(true)} className="btn-contorno mt-4 min-h-10 px-4 text-sm">
        Novo grupo
      </button>
    );
  }
  const enviar = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!pode) return;
    setSalvando(true);
    const r = await criarGrupo(slug, nome.trim(), descricao.trim(), motivo.trim());
    setSalvando(false);
    if (!r.ok) return setRecusa(r);
    setNome(""); setDescricao(""); setMotivo(""); setRecusa(null); setAberto(false);
    await aoCriar(slug);
  };
  return (
    <form onSubmit={enviar} className="mt-4 grid gap-3 border-t border-borda pt-4 sm:grid-cols-2" data-form="grupo">
      <label className="flex flex-col gap-1 text-sm text-cinza">
        Nome
        <input className={CAMPO_BASE} value={nome} onChange={(e) => setNome(e.target.value)} placeholder="ex.: Financeiro" />
        {nome.trim() && (
          <span className={`text-xs ${repetido || !/^[a-z]/.test(slug) ? "text-erro-tinta" : "text-cinza"}`}>
            {repetido
              ? "já existe um grupo com este nome."
              : !/^[a-z]/.test(slug)
                ? "o nome precisa começar por uma letra."
                : `identificador: ${slug}`}
          </span>
        )}
      </label>
      <label className="flex flex-col gap-1 text-sm text-cinza">
        Para que serve
        <input className={CAMPO_BASE} value={descricao} onChange={(e) => setDescricao(e.target.value)} />
      </label>
      <label className="flex flex-col gap-1 text-sm text-cinza sm:col-span-2">
        Motivo (obrigatório)
        <input className={CAMPO_BASE} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
      </label>
      <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
        <button type="submit" disabled={!pode} className={pode ? "pill-cta min-h-11 px-5" : "pill-desligada min-h-11 px-5"}>
          {salvando ? "Criando…" : "Criar e escolher as abas"}
        </button>
        <button type="button" onClick={() => setAberto(false)} className="btn-contorno min-h-11 px-4 text-sm">Cancelar</button>
        {recusa && <p role="status" className="text-sm text-erro-tinta">{recusa.detalhe}</p>}
      </div>
    </form>
  );
}

/**
 * Os masters: o único papel que sobrou. Passam por todas as abas sem grupo e
 * são os únicos que mudam esta tela. Tirar pede confirmação com motivo; o
 * banco recusa tirar o último que consegue entrar.
 */
function Masters({ matriz, aoMudar }: { matriz: Matriz; aoMudar: () => Promise<void> }) {
  const [tirando, setTirando] = useState<string | null>(null);
  const [motivoTirar, setMotivoTirar] = useState("");
  const [email, setEmail] = useState("");
  const [motivo, setMotivo] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [resposta, setResposta] = useState<RespostaDePermissao | null>(null);

  const executar = async (fn: () => Promise<RespostaDePermissao>) => {
    setSalvando(true);
    const r = await fn();
    setSalvando(false);
    setResposta(r);
    if (r.ok && r.mudou) await aoMudar();
    return r;
  };

  const tirar = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!tirando) return;
    const r = await executar(() => definirMaster(tirando, false, motivoTirar.trim()));
    if (r.ok) { setTirando(null); setMotivoTirar(""); }
  };
  const dar = async (ev: FormEvent) => {
    ev.preventDefault();
    const r = await executar(() => definirMaster(email.trim(), true, motivo.trim()));
    if (r.ok) { setEmail(""); setMotivo(""); }
  };
  const podeDar = email.trim().length > 3 && motivo.trim().length > 0 && !salvando;

  return (
    <Caixa titulo="Masters" dado="masters">
      <p className="mb-3 text-sm text-cinza">
        Passam por todas as abas sem precisar de grupo, e são os únicos que mudam esta tela. Master é
        acesso a tudo — dê só a quem administra o painel.
      </p>
      <ul className="space-y-2 text-sm">
        {matriz.masters.map((p) => (
          <li key={p.email} data-master={p.email}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-tinta">{p.nome}</span>
              <span className="text-cinza">{p.email}</span>
              {!p.tem_login && (
                <span className="rounded-full bg-chip px-2 py-0.5 text-xs text-cinza">ainda não entrou no hub</span>
              )}
              {tirando !== p.email && (
                <button type="button" onClick={() => { setTirando(p.email); setResposta(null); }}
                  className="text-sm font-semibold text-link underline-offset-2 hover:underline">
                  tirar master
                </button>
              )}
            </div>
            {tirando === p.email && (
              <form onSubmit={tirar} className="mt-2 flex flex-wrap items-end gap-2 rounded-cf border border-alerta-tinta/50 bg-alerta-fundo p-3" data-tirando-master>
                <label className="flex min-w-[16rem] flex-1 flex-col gap-1 text-sm text-tinta">
                  Por que {p.nome} deixa de ser master? (obrigatório)
                  <input className={CAMPO_BASE} value={motivoTirar} onChange={(e) => setMotivoTirar(e.target.value)} />
                </label>
                <button type="submit" disabled={!motivoTirar.trim() || salvando}
                  className={motivoTirar.trim() && !salvando ? "pill-cta min-h-12 px-5" : "pill-desligada min-h-12 px-5"}>
                  Tirar master
                </button>
                <button type="button" onClick={() => setTirando(null)} className="btn-contorno min-h-12 px-4 text-sm">Voltar</button>
              </form>
            )}
          </li>
        ))}
      </ul>

      <form onSubmit={dar} className="mt-4 grid gap-3 border-t border-borda pt-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end" data-form="master">
        <label className="flex flex-col gap-1 text-sm text-cinza">
          E-mail de quem vira master
          <input className={CAMPO_BASE} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-sm text-cinza">
          Motivo (obrigatório)
          <input className={CAMPO_BASE} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </label>
        <button type="submit" disabled={!podeDar} className={podeDar ? "pill-cta min-h-12 px-5" : "pill-desligada min-h-12 px-5"}>
          Tornar master
        </button>
      </form>
      {resposta && (
        <p role="status" data-resposta={resposta.ok ? "ok" : "recusa"}
          className={`mt-2 text-sm ${resposta.ok ? "text-aviso-tinta" : "text-erro-tinta"}`}>
          {resposta.detalhe ?? (resposta.ok ? "feito." : "não foi possível.")}
          {resposta.ok && resposta.sem_login && " Ainda não entrou no hub — vale quando entrar."}
        </p>
      )}
    </Caixa>
  );
}

function FormExcecao({ areas, aoMudar }: { areas: AreaDoPainel[]; aoMudar: () => Promise<void> }) {
  const [email, setEmail] = useState("");
  const [area, setArea] = useState(areas[0]?.id ?? "");
  const [nivel, setNivel] = useState<NivelDeAcesso | "tirar">("ver");
  const [motivo, setMotivo] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [resposta, setResposta] = useState<RespostaDePermissao | null>(null);
  const pode = email.trim().length > 3 && !!area && motivo.trim().length > 0 && !salvando;

  const enviar = async (ev: FormEvent) => {
    ev.preventDefault();
    setSalvando(true);
    const r = await definirExcecao(email.trim(), area, nivel === "tirar" ? null : nivel, motivo.trim());
    setSalvando(false);
    setResposta(r);
    if (r.ok && r.mudou) await aoMudar();
    if (r.ok) { setEmail(""); setMotivo(""); }
  };

  return (
    <form onSubmit={enviar} className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-form="excecao">
      <label className="flex flex-col gap-1 text-sm text-cinza">
        E-mail da pessoa
        <input className={CAMPO_BASE} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </label>
      <label className="flex flex-col gap-1 text-sm text-cinza">
        Aba
        <select className={CAMPO_BASE} value={area} onChange={(e) => setArea(e.target.value)}>
          {areas.map((a) => <option key={a.id} value={a.id}>{a.rotulo}</option>)}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-sm text-cinza">
        Nível, para esta pessoa
        <select className={CAMPO_BASE} value={nivel} onChange={(e) => setNivel(e.target.value as NivelDeAcesso | "tirar")}>
          <option value="nenhum">Nenhum (tira o que o grupo dá)</option>
          <option value="ver">Ver</option>
          <option value="editar">Editar</option>
          <option value="tirar">Tirar a exceção (volta ao grupo)</option>
        </select>
      </label>
      <label className="flex flex-col gap-1 text-sm text-cinza">
        Motivo
        <input className={CAMPO_BASE} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
      </label>
      <div className="sm:col-span-2 lg:col-span-4">
        <button type="submit" disabled={!pode} className={pode ? "pill-cta min-h-11 px-5" : "pill-desligada min-h-11 px-5"}>
          {salvando ? "Salvando…" : "Salvar exceção"}
        </button>
        {resposta && (
          <p role="status" data-resposta={resposta.ok ? "ok" : "recusa"}
            className={`mt-2 text-sm ${resposta.ok ? "text-aviso-tinta" : "text-erro-tinta"}`}>
            {resposta.detalhe ?? (resposta.ok ? "feito." : "não foi possível.")}
          </p>
        )}
      </div>
    </form>
  );
}
